
import { errorTrackingService } from './errorTrackingService';
import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabaseClient';

export interface KarmaAction {
  id: string;
  playerId: string;
  actionType: 'combat' | 'social' | 'story' | 'trade' | 'meditation';
  karmaChange: number;
  description: string;
  affectedEntities: string[];
  rippleEffects?: RippleEffect[];
  timestamp: string;
}

export interface RippleEffect {
  degree: number; // 1-7 degrees of separation
  targetId: string;
  targetType: 'player' | 'npc' | 'environment';
  effect: string;
  karmaImpact: number;
}

export interface KarmaBalance {
  current: number;
  lifetime: number;
  positive: number;
  negative: number;
  neutral: number;
  level: 'corrupted' | 'dark' | 'balanced' | 'light' | 'enlightened';
}

class KarmaService {
    private supabase: SupabaseClient;
    
    constructor() {
        this.supabase = supabaseClient;
    }
    
    getSupabase() {
        return this.supabase;
    }
  private readonly KARMA_LEVELS = {
    corrupted: { min: -Infinity, max: -50 },
    dark: { min: -50, max: -20 },
    balanced: { min: -20, max: 20 },
    light: { min: 20, max: 50 },
    enlightened: { min: 50, max: Infinity }
  };

  /**
   * Diamond Cutter principle: Every action creates interconnected effects
   */
  async recordKarmaAction(
    playerId: string,
    actionType: KarmaAction['actionType'],
    baseKarmaChange: number,
    description: string,
    affectedEntities: string[] = []
  ): Promise<KarmaAction> {
    try {
      // Calculate ripple effects based on Diamond Cutter Sutra
      const rippleEffects = this.calculateRippleEffects(
        playerId,
        baseKarmaChange,
        affectedEntities
      );

      const karmaAction: Omit<KarmaAction, 'id' | 'timestamp'> = {
        playerId,
        actionType,
        karmaChange: baseKarmaChange,
        description,
        affectedEntities,
        rippleEffects
      };

      // Record in database
      const { data, error } = await this.supabase
        .from('karma_ledger')
        .insert({
          player_id: playerId,
          action_type: actionType,
          karma_change: baseKarmaChange,
          affected_entities: affectedEntities,
          ripple_effects: rippleEffects,
          description
        })
        .select()
        .single();

      if (error) {
        // Production RLS is currently restrictive – gracefully degrade instead of breaking gameplay
        const errorCode = (error as any).code;
        const errorMessage = error.message || '';
        if (errorCode === '42501' || errorMessage.includes('row-level security')) {
          console.warn('[KarmaService] Karma ledger write blocked by RLS, continuing without persistence');
          return {
            id: `rls_${Date.now()}`,
            ...karmaAction,
            timestamp: new Date().toISOString()
          };
        }
        throw error;
      }

      // Update player's karma balance
      await this.updatePlayerKarma(playerId, baseKarmaChange);

      // Process ripple effects asynchronously
      this.processRippleEffects(rippleEffects);

      return {
        id: data.id,
        ...karmaAction,
        timestamp: data.created_at
      };
    } catch (error) {
      errorTrackingService.logError(error as Error, {
        action: 'recordKarmaAction',
        userId: playerId
      });
      throw error;
    }
  }

  /**
   * Heart Sutra principle: Calculate effects through emptiness
   */
  private calculateRippleEffects(
    sourcePlayerId: string,
    karmaChange: number,
    directTargets: string[]
  ): RippleEffect[] {
    const effects: RippleEffect[] = [];
    
    // Each degree of separation reduces impact
    for (let degree = 1; degree <= 7; degree++) {
      const impact = karmaChange / (degree * degree);
      
      // Skip negligible effects
      if (Math.abs(impact) < 0.1) continue;

      // For now, simulate ripple effects
      // In production, this would query actual relationships
      const targetsAtDegree = this.getEntitiesAtDegree(
        sourcePlayerId,
        directTargets,
        degree
      );

      targetsAtDegree.forEach(target => {
        effects.push({
          degree,
          targetId: target.id,
          targetType: target.type,
          effect: this.describeEffect(impact, target.type),
          karmaImpact: Math.round(impact * 10) / 10
        });
      });
    }

    return effects;
  }

  /**
   * Get entities at specific degree of separation
   */
  private getEntitiesAtDegree(
    sourceId: string,
    directTargets: string[],
    degree: number
  ): Array<{ id: string; type: 'player' | 'npc' | 'environment' }> {
    // Simplified for MVP - in production, use graph database
    if (degree === 1) {
      return directTargets.map(id => ({
        id,
        type: 'player' as const
      }));
    }

    // Simulate expanding ripples
    const entities: Array<{ id: string; type: 'player' | 'npc' | 'environment' }> = [];
    
    // Add some NPCs
    for (let i = 0; i < Math.max(1, 5 - degree); i++) {
      entities.push({
        id: `npc_${degree}_${i}`,
        type: 'npc'
      });
    }

    // Environmental effects at higher degrees
    if (degree >= 4) {
      entities.push({
        id: `env_${degree}`,
        type: 'environment'
      });
    }

    return entities;
  }

  /**
   * Describe the ripple effect based on karma and target
   */
  private describeEffect(
    karmaImpact: number,
    targetType: 'player' | 'npc' | 'environment'
  ): string {
    const isPositive = karmaImpact > 0;
    const magnitude = Math.abs(karmaImpact);

    switch (targetType) {
      case 'player':
        if (isPositive) {
          return magnitude > 5 ? 'Inspired to perform good deeds' : 'Feels slightly uplifted';
        } else {
          return magnitude > 5 ? 'Influenced toward darker choices' : 'Feels minor unease';
        }
      
      case 'npc':
        if (isPositive) {
          return magnitude > 3 ? 'Becomes more helpful' : 'Slightly friendlier';
        } else {
          return magnitude > 3 ? 'Becomes hostile' : 'More suspicious';
        }
      
      case 'environment':
        if (isPositive) {
          return 'Reality stabilizes, new paths open';
        } else {
          return 'Reality tears, void creatures stir';
        }
    }
  }

  /**
   * Process ripple effects on affected entities
   */
  private async processRippleEffects(effects: RippleEffect[]) {
    // Process asynchronously to not block main action
    setTimeout(async () => {
      for (const effect of effects) {
        try {
          if (effect.targetType === 'player' && effect.karmaImpact !== 0) {
            // Update other players' karma
            await this.updatePlayerKarma(effect.targetId, effect.karmaImpact);
          }
          // TODO: Implement NPC and environment effects
        } catch (error) {
          console.error('Failed to process ripple effect:', error);
        }
      }
    }, 100);
  }

  /**
   * Update player's karma balance
   */
  async updatePlayerKarma(playerId: string, change: number): Promise<void> {
    try {
      // Get current karma
      const { data: profile } = await this.supabase
        .from('user_profiles')
        .select('karma')
        .eq('id', playerId)
        .single();

      if (!profile) return;

      const currentKarma = profile.karma || 0;
      const newKarma = currentKarma + change;

      // Update karma
      await this.supabase
        .from('user_profiles')
        .update({
          karma: newKarma
        })
        .eq('id', playerId);

      // Check for world state changes
      await this.checkWorldBalance();
    } catch (error) {
      console.error('Failed to update player karma:', error);
    }
  }

  /**
   * Get player's karma balance and level
   */
  async getKarmaBalance(playerId: string): Promise<KarmaBalance> {
    try {
      // Get current karma from profile
      const { data: profile } = await this.supabase
        .from('user_profiles')
        .select('karma')
        .eq('id', playerId)
        .single();

      const currentKarma = profile?.karma || 0;

      const level = this.getKarmaLevel(currentKarma);

      return {
        current: currentKarma,
        lifetime: 0, // This will be calculated differently now
        positive: 0,
        negative: 0,
        neutral: 0,
        level
      };
    } catch (error) {
      errorTrackingService.logError(error as Error, {
        action: 'getKarmaBalance',
        userId: playerId
      });
      
      // Return default balance
      return {
        current: 0,
        lifetime: 0,
        positive: 0,
        negative: 0,
        neutral: 0,
        level: 'balanced'
      };
    }
  }

  /**
   * Determine karma level based on current value
   */
  private getKarmaLevel(karma: number): KarmaBalance['level'] {
    for (const [level, range] of Object.entries(this.KARMA_LEVELS)) {
      if (karma >= range.min && karma <= range.max) {
        return level as KarmaBalance['level'];
      }
    }
    return 'balanced';
  }

  /**
   * Check and update world balance based on collective karma
   */
  private async checkWorldBalance(): Promise<void> {
    try {
      // Get aggregate karma
      const { data } = await this.supabase
        .from('user_profiles')
        .select('karma');

      if (!data) return;

      let totalKarma = 0;
      let playerCount = 0;

      data.forEach(profile => {
        if (profile.karma !== undefined) {
          totalKarma += profile.karma;
          playerCount++;
        }
      });

      const averageKarma = playerCount > 0 ? totalKarma / playerCount : 0;
      
      // Update world state based on average karma
      await this.updateWorldState(averageKarma, totalKarma);
    } catch (error) {
      console.error('Failed to check world balance:', error);
    }
  }

  /**
   * Update world state based on collective karma
   */
  private async updateWorldState(averageKarma: number, totalKarma: number): Promise<void> {
    let worldState: 'void_invasion' | 'balanced' | 'time_loops';
    
    if (averageKarma < -20) {
      worldState = 'void_invasion';
    } else if (averageKarma > 20) {
      worldState = 'time_loops';
    } else {
      worldState = 'balanced';
    }

    // Store world state
    await this.supabase
      .from('world_state')
      .upsert({
        id: 'current',
        state: worldState,
        average_karma: averageKarma,
        total_karma: totalKarma,
        updated_at: new Date().toISOString()
      });

    // Emit real-time event for world state change
    // This would trigger UI updates and gameplay changes
  }

  /**
   * Apply karma modifiers to combat actions
   */
  calculateCombatKarmaModifier(
    attackType: 'pure' | 'wrathful' | 'compassionate' | 'void',
    playerKarma: number
  ): { damageMultiplier: number; karmaChange: number; sideEffect?: string } {
    switch (attackType) {
      case 'pure':
        return { damageMultiplier: 1, karmaChange: 0 };
      
      case 'wrathful':
        return {
          damageMultiplier: 2,
          karmaChange: -5,
          sideEffect: 'Self-corruption damage'
        };
      
      case 'compassionate':
        return {
          damageMultiplier: 0.8,
          karmaChange: 3,
          sideEffect: 'Heals nearby allies'
        };
      
      case 'void':
        // Emptiness-based attack
        return {
          damageMultiplier: 1.5,
          karmaChange: 0,
          sideEffect: 'Bypasses all defenses, costs HP'
        };
    }
  }

  /**
   * Calculate item value based on karma and emptiness
   */
  calculateItemValue(
    baseValue: number,
    buyerKarma: number,
    marketDemand: number,
    maxDemand: number = 100
  ): number {
    // Heart Sutra principle: less demand = more empty = higher value
    const emptynessFactor = 1 - (marketDemand / maxDemand);
    const karmaMultiplier = 1 + (Math.abs(buyerKarma) / 100);
    
    return Math.round(baseValue * (1 + emptynessFactor) * karmaMultiplier);
  }
}

// Export singleton instance
export const karmaService = new KarmaService();
